// Helpers for keeping the library lists up to date locally (no refetch) after a save.

const toFlag = (value) =>
  value === true || value === 1 || value === '1' || value === 'true' ? 1 : 0

const toId = (value) =>
  value === null ||
  value === undefined ||
  value === '' ||
  value === 'null' ||
  value === 'lib' ||
  Number(value) === 0
    ? null
    : Number(value)

// The API answers saves with booleans / strings ("true", "5", "null"), while the
// list endpoints return 0/1 and numbers. Normalise so both look the same.
export const normalizeLibraryItem = (item) => {
  const normalized = { ...item }

  if ('id' in normalized) normalized.id = Number(normalized.id)
  if ('parent_id' in normalized) normalized.parent_id = toId(normalized.parent_id)
  if ('new_tab' in normalized) normalized.new_tab = toFlag(normalized.new_tab)
  if ('show_print_download' in normalized) {
    normalized.show_print_download = toFlag(normalized.show_print_download)
  }
  if ('is_note' in normalized) normalized.is_note = toFlag(normalized.is_note)
  if ('language' in normalized) {
    normalized.language =
      normalized.language === null ||
      normalized.language === undefined ||
      normalized.language === ''
        ? null
        : Number(normalized.language)
  }

  return normalized
}

export const isSameFolder = (a, b) => toId(a) === toId(b)

// Same order the API uses: position ASC (NULL first), type DESC, name ASC.
export const compareLibraryItems = (a, b) => {
  const positionA = a.position ?? null
  const positionB = b.position ?? null

  if (positionA !== positionB) {
    if (positionA === null) return -1
    if (positionB === null) return 1
    return positionA - positionB
  }

  if (a.type !== b.type) return a.type < b.type ? 1 : -1

  return String(a.name || '').localeCompare(String(b.name || ''), undefined, {
    sensitivity: 'base',
  })
}

const insertSorted = (list, item) => {
  const index = list.findIndex((existing) => compareLibraryItems(item, existing) < 0)
  if (index === -1) return [...list, item]
  return [...list.slice(0, index), item, ...list.slice(index)]
}

// Applies a saved item to the list shown in the current folder.
export const upsertInFolderList = (list, item, belongsHere) => {
  const index = list.findIndex((existing) => existing.id === item.id)

  if (!belongsHere) {
    return index === -1 ? list : list.filter((_, i) => i !== index)
  }

  if (index === -1) return insertSorted(list, item)

  const existing = list[index]
  if (existing.name === item.name && existing.type === item.type) {
    // Order can't have changed, keep the item where it is.
    const next = [...list]
    next[index] = item
    return next
  }

  return insertSorted(
    list.filter((_, i) => i !== index),
    item,
  )
}

// Applies a saved item to the flat list of every library item.
export const upsertInAllList = (list, item) => {
  const index = list.findIndex((existing) => existing.id === item.id)
  if (index === -1) return [...list, item]
  const next = [...list]
  next[index] = item
  return next
}

// Ids of every item nested (at any depth) under the given folder id.
export const getDescendantIds = (list, folderId) => {
  const ids = []
  const queue = [folderId]

  while (queue.length) {
    const current = queue.shift()
    list.forEach((item) => {
      if (item.parent_id === current && !ids.includes(item.id)) {
        ids.push(item.id)
        queue.push(item.id)
      }
    })
  }

  return ids
}
